<template>
  <div class="page home">
    <section class="hero-wrap">
      <div class="hero">
        <div class="hero__bg" />
        <div class="hero__cloud cn-cloud-pattern" aria-hidden="true" />
        <div class="hero__corner hero__corner--tl" aria-hidden="true" />
        <div class="hero__corner hero__corner--tr" aria-hidden="true" />
        <div class="hero__corner hero__corner--bl" aria-hidden="true" />
        <div class="hero__corner hero__corner--br" aria-hidden="true" />
        <div class="hero__content">
          <p class="hero__badge cn-seal-badge">西安本地官方直营</p>
          <h1>百年票票</h1>
          <chinese-divider light />
          <p class="hero__slogan">{{ slogan }}</p>
          <p class="hero__intro">{{ intro }}</p>
          <div class="hero__actions">
            <el-button type="danger" size="medium" round @click="$router.push('/service#ticket')">
              官方购票
            </el-button>
            <el-button size="medium" round plain class="hero__btn-outline" @click="$router.push('/performance')">
              浏览演艺
            </el-button>
          </div>
        </div>
      </div>
    </section>

    <section class="highlights-wrap">
      <chinese-divider class="highlights-wrap__divider" />
      <div class="highlights">
        <div class="highlights__inner">
          <div v-for="item in highlights" :key="item.label" class="highlight-item">
            <i :class="item.icon" />
            <div>
              <strong>{{ item.label }}</strong>
              <span>{{ item.desc }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="section">
      <div class="section__inner">
        <div class="section-head">
          <h2>精选演出与线路</h2>
          <p>长恨歌、驼铃传奇、千古情等王牌演艺与精品一日游线路</p>
        </div>
        <el-row v-if="featuredShows.length" :gutter="24" v-loading="showsLoading">
          <el-col v-for="(item, index) in featuredShows" :key="item.id" :xs="24" :sm="12" :md="8">
            <performance-card :item="item" :color-index="index" />
          </el-col>
        </el-row>
        <div v-else-if="!showsLoading" class="page-empty page-empty--inline">
          <p>暂无演艺内容</p>
          <el-button type="text" @click="$router.push('/performance')">前往演艺介绍</el-button>
        </div>
        <div v-if="featuredShows.length" class="section-more">
          <el-button type="text" class="more-link" @click="$router.push('/performance')">查看全部演艺 →</el-button>
        </div>
      </div>
    </section>

    <section class="section section--alt">
      <div class="section__inner">
        <div class="section-head">
          <h2>最新资讯</h2>
          <p>演出公告、线路动态与出行提示，助您规划完美行程</p>
        </div>
        <div v-if="latestNews.length" class="news-showcase" v-loading="newsLoading">
          <article
            v-if="featuredNews"
            class="news-featured"
            @click="goNewsDetail(featuredNews.id)"
          >
            <div
              class="news-featured__cover"
              :class="{ 'is-placeholder': !featuredNews.coverImage }"
              :style="coverStyle(featuredNews.coverImage)"
            >
              <i v-if="!featuredNews.coverImage" class="el-icon-picture-outline" />
            </div>
            <div class="news-featured__body">
              <div class="news-featured__meta">
                <el-tag v-if="featuredNews.tag" size="mini" type="danger" effect="plain">{{ featuredNews.tag }}</el-tag>
                <span class="news-featured__date">{{ formatDate(featuredNews.publishTime) }}</span>
              </div>
              <h3>{{ featuredNews.title }}</h3>
              <p>{{ featuredNews.summary || '点击查看资讯详情' }}</p>
              <span class="news-featured__more">阅读全文 →</span>
            </div>
          </article>
          <div v-if="moreNews.length" class="news-grid">
            <article
              v-for="item in moreNews"
              :key="item.id"
              class="news-grid-card"
              @click="goNewsDetail(item.id)"
            >
              <div
                class="news-grid-card__cover"
                :class="{ 'is-placeholder': !item.coverImage }"
                :style="coverStyle(item.coverImage)"
              >
                <i v-if="!item.coverImage" class="el-icon-document" />
              </div>
              <div class="news-grid-card__body">
                <div class="news-grid-card__meta">
                  <el-tag v-if="item.tag" size="mini" type="danger" effect="plain">{{ item.tag }}</el-tag>
                  <span>{{ formatDate(item.publishTime) }}</span>
                </div>
                <h4>{{ item.title }}</h4>
                <p>{{ item.summary || '点击查看资讯详情' }}</p>
              </div>
            </article>
          </div>
        </div>
        <div v-else-if="!newsLoading" class="page-empty page-empty--inline">
          <p>暂无资讯</p>
        </div>
        <div class="section-more">
          <el-button type="text" class="more-link" @click="$router.push('/news')">查看更多资讯 →</el-button>
        </div>
      </div>
    </section>

    <section class="section cta">
      <div class="cta__inner">
        <div class="cta__text">
          <h2>开启您的西安之旅</h2>
          <p>关注官方公众号或添加企业微信客服，获取最新特惠与出行咨询</p>
        </div>
        <el-button type="danger" size="medium" round @click="$router.push('/service')">
          前往服务中心
        </el-button>
      </div>
    </section>
  </div>
</template>

<script>
import PerformanceCard from '@/components/PerformanceCard.vue';
import ChineseDivider from '@/components/ChineseDivider.vue';
import { getFeaturedPerformances } from '@/api/performance';
import { getLatestNews } from '@/api/news';
import { SITE_SLOGAN, SITE_INTRO } from '@/constants/portal';
import { formatDate } from '@/utils/format';
import { resolveImageUrl } from '@/utils/image';

export default {
  name: 'Home',
  components: { PerformanceCard, ChineseDivider },
  data() {
    return {
      slogan: SITE_SLOGAN,
      intro: SITE_INTRO,
      featuredShows: [],
      latestNews: [],
      showsLoading: false,
      newsLoading: false,
      highlights: [
        { icon: 'el-icon-medal', label: '官方直营', desc: '正规渠道，安心购票' },
        { icon: 'el-icon-star-on', label: '王牌演艺', desc: '三大实景演出特惠' },
        { icon: 'el-icon-map-location', label: '精品线路', desc: '一日游精心安排' },
        { icon: 'el-icon-service', label: '专属客服', desc: '企微在线随时咨询' }
      ]
    };
  },
  computed: {
    featuredNews() {
      return this.latestNews[0] || null;
    },
    moreNews() {
      return this.latestNews.slice(1);
    }
  },
  created() {
    this.loadShows();
    this.loadNews();
  },
  methods: {
    formatDate,
    coverStyle(src) {
      if (!src) return {};
      return { backgroundImage: 'url(' + resolveImageUrl(src) + ')' };
    },
    loadShows() {
      this.showsLoading = true;
      getFeaturedPerformances(6)
        .then(res => {
          this.featuredShows = res.data || [];
        })
        .catch(() => {
          this.featuredShows = [];
        })
        .finally(() => {
          this.showsLoading = false;
        });
    },
    loadNews() {
      this.newsLoading = true;
      getLatestNews(5)
        .then(res => {
          this.latestNews = res.data || [];
        })
        .catch(() => {
          this.latestNews = [];
        })
        .finally(() => {
          this.newsLoading = false;
        });
    },
    goNewsDetail(id) {
      this.$router.push('/news/' + id);
    }
  }
};
</script>

<style scoped>
.hero-wrap {
  width: 100%;
}
.home .hero {
  position: relative;
  color: #fff;
  padding: 72px 24px 64px;
  text-align: center;
  overflow: hidden;
}
.hero__bg {
  position: absolute;
  inset: 0;
  background:
    linear-gradient(135deg, rgba(74, 14, 28, 0.88) 0%, rgba(139, 21, 56, 0.82) 40%, rgba(196, 30, 58, 0.78) 100%),
    url('~@/assets/images/login-background.png') center / cover no-repeat;
}
.hero__cloud {
  position: absolute;
  inset: 0;
  opacity: 0.35;
  pointer-events: none;
}
.hero__corner {
  position: absolute;
  width: 64px;
  height: 64px;
  border: 1px solid rgba(212, 160, 74, 0.4);
  pointer-events: none;
  z-index: 1;
}
.hero__corner--tl { top: 20px; left: 24px; border-right: none; border-bottom: none; }
.hero__corner--tr { top: 20px; right: 24px; border-left: none; border-bottom: none; }
.hero__corner--bl { bottom: 20px; left: 24px; border-right: none; border-top: none; }
.hero__corner--br { bottom: 20px; right: 24px; border-left: none; border-top: none; }
.hero__content {
  position: relative;
  z-index: 2;
  max-width: 820px;
  margin: 0 auto;
}
.hero__badge {
  margin: 0 0 16px;
}
.hero h1 {
  margin: 0 0 4px;
  font-size: 44px;
  font-weight: 700;
  letter-spacing: 10px;
  text-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}
.hero__slogan {
  margin: 0 0 14px;
  font-size: 17px;
  letter-spacing: 2px;
  opacity: 0.95;
}
.hero__intro {
  margin: 0 0 28px;
  font-size: 14px;
  line-height: 1.9;
  opacity: 0.88;
  max-width: 680px;
  margin-left: auto;
  margin-right: auto;
}
.hero__actions {
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
}
.hero__btn-outline {
  color: #fff !important;
  border-color: rgba(255, 255, 255, 0.6) !important;
  background: transparent !important;
}
.hero__btn-outline:hover {
  background: rgba(255, 255, 255, 0.12) !important;
  border-color: #fff !important;
}
.highlights-wrap {
  width: 100%;
  background: #fff;
  border-top: 1px solid #ebeef5;
  border-bottom: 1px solid #ebeef5;
  position: relative;
}
.highlights-wrap__divider {
  padding-top: 14px;
}
.highlights {
  max-width: 1200px;
  margin: 0 auto;
}
.highlights__inner {
  padding: 28px 24px;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}
.highlight-item {
  display: flex;
  align-items: center;
  gap: 14px;
  position: relative;
  padding: 12px 8px;
  border-radius: 4px;
  transition: background 0.2s;
}
.highlight-item:hover {
  background: #fef8f4;
}
.highlight-item i {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 24px;
  color: #c41e3a;
  border: 1px solid rgba(212, 160, 74, 0.35);
  border-radius: 4px;
  background: linear-gradient(135deg, #fff9f0, #fef0f2);
}
.highlight-item strong {
  display: block;
  font-size: 15px;
  color: #303133;
  margin-bottom: 2px;
}
.highlight-item span {
  font-size: 12px;
  color: #909399;
}
.section {
  padding: 48px 24px;
}
.section--alt {
  background: #fff;
}
.section__inner {
  max-width: 1200px;
  margin: 0 auto;
}
.section-more {
  margin-top: 20px;
  text-align: center;
}
.cta {
  background:
    linear-gradient(135deg, #fef0f2, #fff8f0),
    url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='50' viewBox='0 0 100 50'%3E%3Cpath d='M0 32c10-10 20-10 30 0s20 10 30 0' fill='none' stroke='%23d4a04a' stroke-opacity='0.08' stroke-width='1'/%3E%3C/svg%3E");
  background-size: auto, 100px 50px;
  border-top: 1px solid rgba(212, 160, 74, 0.2);
  border-bottom: 1px solid rgba(212, 160, 74, 0.2);
}
.cta__text h2 {
  letter-spacing: 2px;
}
.cta__inner {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}
.cta__text h2 {
  margin: 0 0 8px;
  font-size: 22px;
  color: #303133;
}
.cta__text p {
  margin: 0;
  color: #909399;
  font-size: 14px;
}
.news-showcase {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.news-featured {
  display: flex;
  gap: 24px;
  padding: 20px;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.2s;
}
.news-featured:hover {
  box-shadow: 0 8px 28px rgba(196, 30, 58, 0.1);
  transform: translateY(-2px);
}
.news-featured__cover {
  width: 42%;
  min-height: 220px;
  flex-shrink: 0;
  border-radius: 6px;
  background-size: cover;
  background-position: center;
}
.news-featured__cover.is-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #fef0f2 0%, #f8e8ec 100%);
  color: #e8b4bc;
  font-size: 48px;
}
.news-featured__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.news-featured__meta {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}
.news-featured__date {
  font-size: 13px;
  color: #909399;
}
.news-featured h3 {
  margin: 0 0 12px;
  font-size: 22px;
  color: #303133;
  line-height: 1.45;
  font-weight: 600;
}
.news-featured p {
  margin: 0;
  flex: 1;
  font-size: 15px;
  color: #606266;
  line-height: 1.8;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.news-featured__more {
  margin-top: 16px;
  font-size: 14px;
  color: #c41e3a;
  font-weight: 500;
}
.news-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}
.news-grid-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow 0.2s, transform 0.2s;
}
.news-grid-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
  transform: translateY(-2px);
}
.news-grid-card__cover {
  height: 140px;
  background-size: cover;
  background-position: center;
}
.news-grid-card__cover.is-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #fafafa 0%, #f0f2f5 100%);
  color: #dcdfe6;
  font-size: 32px;
}
.news-grid-card__body {
  padding: 16px;
  flex: 1;
  display: flex;
  flex-direction: column;
}
.news-grid-card__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
  font-size: 12px;
  color: #909399;
}
.news-grid-card h4 {
  margin: 0 0 8px;
  font-size: 16px;
  color: #303133;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.news-grid-card p {
  margin: 0;
  font-size: 13px;
  color: #909399;
  line-height: 1.65;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.news-grid-card:hover h4 {
  color: #c41e3a;
}
.more-link {
  color: #c41e3a !important;
  font-size: 14px;
}
.page-empty--inline {
  padding: 24px 0;
  text-align: center;
  color: #c0c4cc;
  font-size: 14px;
}
@media (max-width: 768px) {
  .highlights__inner {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    padding: 20px 16px;
  }
  .home .hero {
    padding: 48px 16px 40px;
  }
  .hero__corner {
    width: 40px;
    height: 40px;
  }
  .hero__corner--tl,
  .hero__corner--tr { top: 12px; }
  .hero__corner--bl,
  .hero__corner--br { bottom: 12px; }
  .hero__corner--tl,
  .hero__corner--bl { left: 12px; }
  .hero__corner--tr,
  .hero__corner--br { right: 12px; }
  .hero h1 {
    font-size: 32px;
    letter-spacing: 6px;
  }
  .hero__slogan {
    font-size: 14px;
    letter-spacing: 1px;
  }
  .hero__intro {
    font-size: 13px;
  }
  .section {
    padding: 36px 16px;
  }
  .cta__inner {
    flex-direction: column;
    text-align: center;
  }
  .news-featured {
    flex-direction: column;
    padding: 16px;
    gap: 16px;
  }
  .news-featured__cover {
    width: 100%;
    min-height: 180px;
  }
  .news-featured h3 {
    font-size: 18px;
  }
  .news-featured p {
    font-size: 14px;
    -webkit-line-clamp: 2;
  }
  .news-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  .news-grid-card__cover {
    height: 160px;
  }
}
</style>
